import { create } from "zustand";

interface User {
    id: string;
    email: string;
    name?: string;
    avatar?: string;
    role: string;
    workspace_id: string;
    workspace_name?: string;
    needs_onboarding?: boolean;
}

interface SessionState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    hasResolvedSession: boolean;
    restoreCachedUser: () => void;
    setUser: (user: User | null) => void;
    setLoading: (loading: boolean) => void;
    logout: () => void;
}

const SESSION_USER_CACHE_KEY = "foxia.session.user";

function readCachedUser(): User | null {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.sessionStorage.getItem(SESSION_USER_CACHE_KEY);
        if (!raw) return null;

        const user = normalizeSessionUser(JSON.parse(raw));
        if (!user) {
            window.sessionStorage.removeItem(SESSION_USER_CACHE_KEY);
            return null;
        }

        return user;
    } catch {
        window.sessionStorage.removeItem(SESSION_USER_CACHE_KEY);
        return null;
    }
}

export function normalizeSessionUser(user: unknown): User | null {
    if (!user || typeof user !== "object") return null;
    const candidate = user as Partial<User>;
    if (typeof candidate.id !== "string" || typeof candidate.email !== "string") {
        return null;
    }
    return candidate as User;
}

function cacheUser(user: User | null) {
    if (typeof window === "undefined") return;

    if (user) {
        window.sessionStorage.setItem(SESSION_USER_CACHE_KEY, JSON.stringify(user));
    } else {
        window.sessionStorage.removeItem(SESSION_USER_CACHE_KEY);
    }
}

export const useSession = create<SessionState>((set) => ({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    hasResolvedSession: false,
    restoreCachedUser: () => {
        const user = readCachedUser();
        if (!user) return;
        set({ user, isAuthenticated: true });
    },
    setUser: (user) =>
        set(() => {
            const nextUser = normalizeSessionUser(user);
            cacheUser(nextUser);
            return {
                user: nextUser,
                isAuthenticated: !!nextUser,
                isLoading: false,
                hasResolvedSession: true,
            };
        }),
    setLoading: (isLoading) => set({ isLoading }),
    logout: () =>
        set(() => {
            cacheUser(null);
            return {
                user: null,
                isAuthenticated: false,
                isLoading: false,
                hasResolvedSession: true,
            };
        }),
}));
